import classnames from 'classnames';
import { QA_FIXTURES_SUMMARY_BLOCK_MATCH_CARD } from '@test-config/testIds';
import FixtureAlertHeader from '@components/atoms/fixture-alert-header';
import { getMatchStatus, MATCH_STATUS } from '@stadionhq-v3/core';
import { Button, Heading } from '@stadionhq-v3/base-ui';
import { LocalizedDate } from '@components/atoms/localized-date';
import { ReactNode } from 'react';
import styles from './fixturesSummaryBlockMatchCard.module.scss';
import { FixturesSummaryBlockMatchCardProps } from './fixturesSummaryBlockMatchCard.types';

const FixturesSummaryBlockMatchCard = ({
  testId = QA_FIXTURES_SUMMARY_BLOCK_MATCH_CARD.COMPONENT,
  classNames = [],
  nativeProps = {},
  labels,
  homeClub,
  awayClub,
  competitionName,
  matchPeriod,
  kickoffTime,
  dateTimeSeparator = '/',
  date,
  ctaLinks,
  venue,
  homeAway,
  locale,
  broadcasters,
}: FixturesSummaryBlockMatchCardProps) => {
  const classes = classnames(
    [styles.fixturesSummaryBlockMatchCard],
    classNames
  );

  const clubCrestTorender = homeClub.isClientTeam
    ? awayClub.crestImage
    : homeClub.crestImage;

  const clubNameToRender = awayClub.isClientTeam
    ? homeClub.name
    : awayClub.name;

  const isPostmatch = getMatchStatus(matchPeriod) === MATCH_STATUS.POST_MATCH;

  return (
    <div {...nativeProps} className={classes} data-testid={testId}>
      <div className={styles.alertHeader}>
        <FixtureAlertHeader matchStatus={matchPeriod} labels={labels} />
      </div>

      <div className={styles.matchDetails}>
        <div className={styles.crestAndCompetition}>
          {clubCrestTorender && (
            <span className={styles.clubCrest}>
              {clubCrestTorender as ReactNode}
            </span>
          )}

          {competitionName && (
            <span className={styles.competitionName}>{competitionName}</span>
          )}
        </div>

        {isPostmatch ? (
          <div className={styles.postMatchTeamResultsContainer}>
            <span className={styles.teamAndScore}>
              <Heading
                classNames={[
                  classnames([
                    styles.clubScore,
                    homeClub.isClientTeam && styles.clientTeamStyling,
                  ]),
                ]}
                as="h5"
              >
                {homeClub.score}
              </Heading>
              <Heading
                classNames={[
                  classnames([
                    styles.clubName,
                    homeClub.isClientTeam && styles.clientTeamStyling,
                  ]),
                ]}
                as="h6"
              >
                {homeClub.name}
              </Heading>
            </span>
            <span className={styles.teamAndScore}>
              <Heading
                classNames={[
                  classnames([
                    styles.clubScore,
                    awayClub.isClientTeam && styles.clientTeamStyling,
                  ]),
                ]}
                as="h6"
              >
                {awayClub.score}
              </Heading>
              <Heading
                classNames={[
                  classnames([
                    styles.clubName,
                    awayClub.isClientTeam && styles.clientTeamStyling,
                  ]),
                ]}
                as="h6"
              >
                {awayClub.name}
              </Heading>
            </span>
          </div>
        ) : (
          <div className={styles.preMatchTeamContainer}>
            <Heading classNames={[classnames([styles.clubName])]} as="h6">
              {matchPeriod === 'PreMatch' ? clubNameToRender : awayClub.name}
            </Heading>
          </div>
        )}

        {broadcasters && !isPostmatch && (
          <div className={classnames(styles.broadcastersWrapper)}>
            {broadcasters}
          </div>
        )}

        <div className={styles.timeAndLocationContainer}>
          <div className={styles.timeAndDate}>
            <LocalizedDate
              date={date}
              locale={locale}
              format="ddd DD MMM YYYY"
              classNames={[classnames(styles.timezone)]}
            />
            {Boolean(date || kickoffTime) && dateTimeSeparator}
            <LocalizedDate
              date={kickoffTime}
              locale={locale}
              format="h:mm A"
              classNames={[classnames(styles.kickOffTime)]}
            />
          </div>

          <div className={styles.indicatorAndVenue}>
            <span className={styles.homeAway}>{homeAway}</span>
            <span className={styles.venue}>{venue}</span>
          </div>
        </div>
      </div>

      {ctaLinks && (
        <div className={classnames(styles.matchCardLinks)}>
          {ctaLinks.map((link) => (
            <Button
              key={`${matchPeriod}-${link.text}`}
              variant="primary"
              {...link}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default FixturesSummaryBlockMatchCard;
